import React from 'react';
import Editor from '@monaco-editor/react';
import { Info } from 'lucide-react';
import { useMonacoTheme } from '../../hooks';

interface ScriptEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  title: string;
  description?: string;
}

// Example scripts
const PRE_REQUEST_EXAMPLE = `// Pre-request Script Example
// Set a timestamp variable
pm.environment.set('timestamp', Date.now().toString());

// Add a custom header
pm.request.headers.add('X-Request-ID', Math.random().toString(36).substr(2, 9));

// Log for debugging
console.log('Request URL:', pm.request.url);`;

const POST_REQUEST_EXAMPLE = `// Tests Example
pm.test('Status is 200', () => {
  pm.expect(pm.response.status).to.equal(200);
});

pm.test('Response has data', () => {
  const json = pm.response.json();
  pm.expect(json).to.have.property('data');
});

pm.test('Response time is acceptable', () => {
  pm.expect(pm.response.responseTime).to.be.below(1000);
});

// Store response data in environment
const data = pm.response.json();
if (data.token) {
  pm.environment.set('authToken', data.token);
}`;

export function ScriptEditor({
  value,
  onChange,
  placeholder,
  title,
  description,
}: ScriptEditorProps) {
  const monacoTheme = useMonacoTheme();
  const isPreRequest = title.toLowerCase().includes('pre');
  const example = isPreRequest ? PRE_REQUEST_EXAMPLE : POST_REQUEST_EXAMPLE;

  const handleInsertExample = () => {
    onChange(example);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">{title}</h3>
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
        </div>
        {!value && (
          <button
            onClick={handleInsertExample}
            className="text-xs text-primary hover:underline"
          >
            Insert Example
          </button>
        )}
      </div>

      <div className="border border-input rounded-md overflow-hidden">
        <Editor
          height="200px"
          language="javascript"
          value={value}
          onChange={(v) => onChange(v || '')}
          theme={monacoTheme}
          options={{
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 13,
            lineNumbers: 'on',
            tabSize: 2,
            wordWrap: 'on',
            automaticLayout: true,
          }}
        />
      </div>

      {!value && (
        <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 rounded-md p-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Available APIs:</p>
            <ul className="mt-1 space-y-0.5">
              <li><code>pm.environment.get/set/unset</code> - Environment variables</li>
              <li><code>pm.globals.get/set/unset</code> - Global variables</li>
              <li><code>pm.request.headers.add/get</code> - Request headers</li>
              {!isPreRequest && (
                <>
                  <li><code>pm.response.json()</code> - Parse response body</li>
                  <li><code>pm.test(name, fn)</code> - Add test case</li>
                  <li><code>pm.expect(value)</code> - Chai-style assertions</li>
                </>
              )}
              <li><code>console.log/info/warn/error</code> - Logging</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScriptEditor;
